import React, { type RefObject } from 'react';
import { ActionIcon, Box, Group, Paper, Stack, Text, UnstyledButton, TextInput, ScrollArea, Center, Badge, Alert, Loader, useMantineColorScheme } from '@mantine/core';
import { PillTabList } from '@shared/ui/PillTabList';
import { IconCheck, IconChevronLeft, IconChevronRight, IconHistory, IconLogout, IconMessage, IconAddressBook, IconBell, IconUsers, IconSparkles, IconCalendarTime, IconSearch, IconUser, IconUserPlus, IconUserSearch, IconX, IconShieldLock, IconFileDescription, IconInfoCircle, IconSun, IconMoon, IconAdjustments, IconRefresh, IconPlus } from '@tabler/icons-react';
import type { AuthSessionUser } from '@domain/auth/types';
import type { ChatSession, GroupMap } from '@domain/chat/types';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { UserPresenceIndicator } from '@shared/ui/UserPresenceIndicator';
import type { QueryState } from '@shared/types/async';
import { formatListTime, initials } from '@shared/utils/ui';
import { EmojiText } from '../../../emoji/EmojiText';
import type { FriendRequest, UserSearchResult } from '@domain/contacts/types';
import type { ContactDirectoryUser } from '@domain/contacts/types';
import type { GroupSearchResult } from '@domain/group/types';
import { getWorkspaceNavigationItems, type ActiveTab } from './workspaceNavigationItems';
import { WorkspaceEmptyState } from './WorkspaceEmptyState';
import type { SettingsSection } from '../types/settings';
import { resolveSessionAvatarUrl } from '../services/sessionAvatarPresentation';

type ContactsSubView = 'list' | 'search' | 'requests' | 'groups' | 'group-search';
export type WorkspaceListPanelProps = {
  currentUser: AuthSessionUser;
  cdnBase: string;
  profileAvatarUrl?: string;
  profileSignature: string;
  isOnline: boolean;
  connState: 'idle' | 'connecting' | 'online' | 'error';
  imError: string | null;
  activeTab: ActiveTab;
  contactsSubView: ContactsSubView;
  directoryFilter: string;
  totalUnreadCount: number;
  visibleSessions: ChatSession[];
  activeSessionKey: string | null;
  visibleContacts: ContactDirectoryUser[];
  groups: GroupMap[number][];
  visibleGroups: GroupMap[number][];
  contactsById: Record<number, ContactDirectoryUser>;
  presenceByUserId: Record<number, number>;
  pendingRequestCount: number;
  searchKeyword: string;
  searchState: QueryState;
  searchError: string | null;
  searchResults: UserSearchResult[];
  sendingRequestByUserId: Record<number, boolean>;
  groupSearchKeyword: string;
  groupSearchResults: GroupSearchResult[];
  groupSearchState: QueryState;
  groupSearchError: string | null;
  friendRequests: FriendRequest[];
  friendRequestsLoading: boolean;
  friendRequestActionById: Record<number, 'approve' | 'reject'>;
  isUserMenuOpen: boolean;
  userMenuRef: RefObject<HTMLDivElement | null>;
  userMenuBtnRef: RefObject<HTMLButtonElement | null>;
  searchInputRef: RefObject<HTMLInputElement | null>;
  onViewUserProfile?: (nameOrId: string | number) => void;
  onNavigate: (value: ActiveTab) => void;
  setIsUserMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveTab: React.Dispatch<React.SetStateAction<ActiveTab>>;
  setContactsSubView: React.Dispatch<React.SetStateAction<ContactsSubView>>;
  setDirectoryFilter: React.Dispatch<React.SetStateAction<string>>;
  setIsPostModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchKeyword: React.Dispatch<React.SetStateAction<string>>;
  setSearchResults: React.Dispatch<React.SetStateAction<UserSearchResult[]>>;
  setSearchState: React.Dispatch<React.SetStateAction<QueryState>>;
  setSearchError: React.Dispatch<React.SetStateAction<string | null>>;
  setFriendRequestTarget: React.Dispatch<React.SetStateAction<UserSearchResult | null>>;
  setFriendRequestNote: React.Dispatch<React.SetStateAction<string>>;
  setIsFriendRequestModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setGroupSearchKeyword: React.Dispatch<React.SetStateAction<string>>;
  setGroupSearchResults: React.Dispatch<React.SetStateAction<GroupSearchResult[]>>;
  setGroupSearchState: React.Dispatch<React.SetStateAction<QueryState>>;
  setGroupSearchError: React.Dispatch<React.SetStateAction<string | null>>;
  setIsCreateGroupModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleLogout: () => void;
  handleReconnectIm: () => void;
  openSession: (sessionKey: string) => void;
  openDirectSession: (contactId: number) => void;
  openConversationDraft: (contact: ContactDirectoryUser) => void;
  openSearchConversationDraft: (contact: UserSearchResult) => void;
  openGroupSession: (groupId: number) => void;
  handleSearchUsers: () => Promise<void>;
  handleSearchGroups: () => Promise<void>;
  handleApproveRequest: (request: FriendRequest) => Promise<void>;
  handleRejectRequest: (request: FriendRequest) => Promise<void>;
  loadFriendRequests: () => Promise<void>;
  mobilePlazaContent?: React.ReactNode;
  groupMemberSummary: (
    group: GroupMap[number] | undefined,
    contactsById: Record<number, ContactDirectoryUser>,
    currentUserId: number,
  ) => string;
  groupsById: GroupMap;
  selectedSettingsSection: SettingsSection | null;
  onSelectSettingsSection: (section: SettingsSection) => void;
};

function SettingsActionItem({
  onClick,
  label,
  icon,
  color,
  danger,
  active,
}: {
  onClick: () => void;
  label: string;
  description?: string;
  icon: React.ReactNode;
  color: string;
  danger?: boolean;
  active?: boolean;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      px="sm"
      py={12}
      style={{
        width: '100%',
        background: active ? 'rgba(185, 87, 54, 0.08)' : 'transparent',
        borderRadius: 'var(--mantine-radius-md)',
        transition: 'background-color 160ms ease',
      }}
      aria-current={active ? 'page' : undefined}
      styles={{
        root: {
          '&:hover': {
            backgroundColor: 'rgba(185, 87, 54, 0.06)',
          }
        }
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        <Center
          w={28}
          h={28}
          c={danger ? '#B33A3A' : color}
        >
          {icon}
        </Center>
        <Text fw={700} size="sm" c={danger ? '#B33A3A' : undefined} style={{ flex: 1, minWidth: 0 }}>{label}</Text>
        <IconChevronRight size={14} color="var(--mantine-color-gray-4)" />
      </Group>
    </UnstyledButton>
  );
}

function SidebarThemeControl() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  return (
    <PillTabList
      value={colorScheme}
      onChange={(val) => setColorScheme(val as 'light' | 'dark' | 'auto')}
      items={[
        { value: 'light', children: '浅色' },
        { value: 'dark', children: '深色' },
        { value: 'auto', children: '跟随系统' },
      ]}
    />
  );
}

function WorkspaceListBottomNav({
  activeTab,
  totalUnreadCount,
  pendingRequestCount,
  onNavigate,
}: {
  activeTab: ActiveTab;
  totalUnreadCount: number;
  pendingRequestCount: number;
  onNavigate: (value: ActiveTab) => void;
}) {
  const navigationItems = getWorkspaceNavigationItems({
    totalUnreadCount,
    pendingRequestCount,
    iconSize: 24,
  });

  return (
    <Box
      className="workspace-list-bottom-nav"
      component="nav"
      aria-label="主菜单"
    >
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${navigationItems.length}, minmax(0, 1fr))`,
          gap: 4,
        }}
      >
        {navigationItems.map((item) => {
          const active = activeTab === item.value;
          return (
            <UnstyledButton
              key={item.value}
              className="workspace-list-bottom-nav__item"
              onClick={() => onNavigate(item.value)}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
              title={item.label}
              px={4}
              py={8}
              style={{
                position: 'relative',
                width: 44,
                height: 44,
                minWidth: 0,
                marginInline: 'auto',
              }}
            >
              <Center>
                <Box
                  pos="relative"
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 30,
                    height: 28,
                  }}
                >
                  {item.icon}
                  {item.badge !== undefined && item.badge > 0 && (
                    <Badge
                      size="xs"
                      circle
                      color="terracotta"
                      variant="filled"
                      style={{
                        position: 'absolute',
                        top: -4,
                        right: -8,
                        pointerEvents: 'none',
                        border: '2px solid var(--locs-panel)',
                      }}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </Badge>
                  )}
                </Box>
              </Center>
            </UnstyledButton>
          );
        })}
      </Box>
    </Box>
  );
}

function DesktopUserMenuItem({
  icon,
  label,
  description,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      px="sm"
      py={9}
      role="menuitem"
      style={{
        display: 'flex',
        width: '100%',
        borderRadius: 10,
        transition: 'background-color 160ms ease',
      }}
      styles={{
        root: {
          '&:hover': {
            backgroundColor: danger ? 'rgba(179, 58, 58, 0.08)' : 'rgba(196, 90, 60, 0.08)',
          },
        },
      }}
    >
      <Group gap="sm" wrap="nowrap" align="center" w="100%">
        <Center c={danger ? '#B33A3A' : 'terracotta.7'}>{icon}</Center>
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={700} c={danger ? '#B33A3A' : undefined}>
            {label}
          </Text>
          <Text size="xs" c={danger ? 'red.7' : 'dimmed'} truncate>
            {description}
          </Text>
        </Stack>
        {!danger && <IconChevronRight size={14} color="var(--mantine-color-dimmed)" />}
      </Group>
    </UnstyledButton>
  );
}

function ThreadItem({
  session,
  avatarUrl,
  presenceStatus,
  active,
  onClick,
}: {
  session: ChatSession;
  avatarUrl: string;
  presenceStatus?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      px="md"
      py="sm"
      className="locs-hover-lift"
      bg={active ? 'var(--mantine-color-default-hover)' : undefined}
      style={{
        width: '100%',
        borderRadius: 12,
        transition: 'background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
      }}
    >
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: '44px minmax(0, 1fr)',
          columnGap: 'var(--mantine-spacing-sm)',
          alignItems: 'center',
        }}
      >
        <Box pos="relative" style={{ alignSelf: 'start' }}>
          <UserAvatar
            id={session.sessionId}
            name={session.title}
            avatarUrl={avatarUrl}
            size={44}
            fallback={session.avatarText}
            avatarKind={session.sessionType === 'group' ? 'group' : 'human'}
          />
          {session.sessionType === 'single' && (
            <Box pos="absolute" right={-1} bottom={-1}>
              <UserPresenceIndicator status={presenceStatus ?? 0} size={10} />
            </Box>
          )}
        </Box>
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              columnGap: 8,
              alignItems: 'center',
            }}
          >
            <Text fw={active ? 800 : 700} size="sm" truncate style={{ minWidth: 0 }}>
              {session.title}
            </Text>
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {formatListTime(session.updatedAt)}
            </Text>
          </Box>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              columnGap: 8,
              alignItems: 'center',
            }}
          >
            <Text size="xs" c={active ? 'terracotta.7' : 'dimmed'} truncate style={{ minWidth: 0 }}>
              <EmojiText text={session.lastMsgPreview || '还没有内容'} />
            </Text>
            {session.unreadCount > 0 && (
              <Badge size="xs" circle color="terracotta">
                {session.unreadCount > 99 ? '99+' : session.unreadCount}
              </Badge>
            )}
          </Box>
        </Stack>
      </Box>
    </UnstyledButton>
  );
}

function ContactsEntryItem({
  icon,
  label,
  badge,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      px="md"
      py="sm"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        borderRadius: 12,
        transition: 'background-color 160ms ease, transform 160ms ease',
      }}
      styles={{
        root: {
          '&:hover': {
            backgroundColor: 'rgba(44, 38, 35, 0.04)',
            transform: 'translateY(-1px)',
          },
        },
      }}
    >
      <Center
        w={32}
        h={32}
        bg="rgba(196, 90, 60, 0.1)"
        c="terracotta.7"
        style={{ borderRadius: 8 }}
      >
        {icon}
      </Center>
      <Text size="sm" fw={700} truncate style={{ flex: 1, minWidth: 0 }}>
        {label}
      </Text>
      {badge !== undefined && badge > 0 && (
        <Badge size="xs" circle color="red">
          {badge}
        </Badge>
      )}
      <IconChevronRight size={14} color="var(--mantine-color-dimmed)" />
    </UnstyledButton>
  );
}

function ProductShortcut({
  title,
  description,
  icon,
  onClick,
  disabled,
  badge,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  badge?: string;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      p="sm"
      style={{
        width: '100%',
        borderRadius: 14,
        border: '1px solid rgba(196, 90, 60, 0.14)',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.92), rgba(253,236,232,0.55))',
        opacity: disabled ? 0.72 : 1,
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
      }}
      styles={{
        root: {
          '&:hover': {
            transform: disabled ? 'none' : 'translateY(-1px)',
            boxShadow: disabled ? 'none' : '0 10px 24px rgba(44, 38, 35, 0.08)',
            borderColor: disabled ? 'rgba(196, 90, 60, 0.14)' : 'rgba(196, 90, 60, 0.28)',
          },
        },
      }}
    >
      <Group wrap="nowrap" gap="sm" align="center">
        <Center
          w={38}
          h={38}
          c="terracotta.7"
          style={{
            flex: '0 0 auto',
            borderRadius: 12,
            backgroundColor: 'rgba(196, 90, 60, 0.1)',
          }}
        >
          {icon}
        </Center>
        <Stack gap={1} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={800} truncate>{title}</Text>
          <Text size="xs" c="dimmed" truncate>{description}</Text>
        </Stack>
        {badge ? (
          <Badge size="xs" variant="light" color="gray" radius="sm">{badge}</Badge>
        ) : (
          <IconChevronRight size={14} color="var(--mantine-color-sand-6)" />
        )}
      </Group>
    </UnstyledButton>
  );
}

function ContactItem({
  id,
  name,
  avatarUrl,
  presenceStatus,
  onClick,
  onStartConversation,
  onViewUserProfile,
}: {
  id: number;
  name: string;
  avatarUrl: string;
  presenceStatus: number;
  onClick: () => void;
  onStartConversation: () => void;
  onViewUserProfile?: (nameOrId: string | number) => void;
}) {
  return (
    <Box
      className="contact-directory-item locs-hover-lift"
      px="sm"
      py="xs"
      style={{
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr) 30px',
        alignItems: 'center',
        columnGap: 'var(--mantine-spacing-sm)',
        width: '100%',
        borderRadius: 'var(--mantine-radius-md)',
        cursor: 'pointer',
      }}
    >
      <UnstyledButton
        pos="relative"
        aria-label={`查看 ${name} 的资料`}
        title={`查看 ${name} 的资料`}
        onClick={(event) => {
          if (onViewUserProfile) {
            event.stopPropagation();
            onViewUserProfile(id);
          }
        }}
        style={{ cursor: 'pointer', zIndex: 5 }}
      >
        <UserAvatar id={id} name={name} avatarUrl={avatarUrl} size={32} avatarKind="human" />
        <Box pos="absolute" bottom={-1} right={-1}>
          <UserPresenceIndicator status={presenceStatus} size={8} />
        </Box>
      </UnstyledButton>
      <UnstyledButton onClick={onClick} style={{ minWidth: 0, textAlign: 'left' }}>
        <Text size="sm" fw={700} truncate>{name}</Text>
      </UnstyledButton>
      <ActionIcon
        onClick={(event) => {
          event.stopPropagation();
          onStartConversation();
        }}
        variant="subtle"
        color="terracotta"
        radius="md"
        size={30}
        aria-label={`向 ${name} 发起咨询`}
        title={`向 ${name} 发起咨询`}
      >
        <IconMessage size={17} stroke={2.2} />
      </ActionIcon>
    </Box>
  );
}

export function WorkspaceListPanel(props: WorkspaceListPanelProps) {
  const {
    currentUser,
    cdnBase,
    profileAvatarUrl,
    profileSignature,
    isOnline,
    connState,
    imError,
    activeTab,
    contactsSubView,
    directoryFilter,
    totalUnreadCount,
    visibleSessions,
    activeSessionKey,
    visibleContacts,
    groups,
    visibleGroups,
    contactsById,
    presenceByUserId,
    pendingRequestCount,
    searchKeyword,
    searchState,
    searchError,
    searchResults,
    sendingRequestByUserId,
    groupSearchKeyword,
    groupSearchResults,
    groupSearchState,
    groupSearchError,
    friendRequests,
    friendRequestsLoading,
    friendRequestActionById,
    isUserMenuOpen,
    userMenuRef,
    userMenuBtnRef,
    searchInputRef,
    setIsUserMenuOpen,
    setActiveTab,
    setContactsSubView,
    setDirectoryFilter,
    setIsPostModalOpen,
    setSearchKeyword,
    setSearchResults,
    setSearchState,
    setSearchError,
    setFriendRequestTarget,
    setFriendRequestNote,
    setIsFriendRequestModalOpen,
    setGroupSearchKeyword,
    setGroupSearchResults,
    setGroupSearchState,
    setGroupSearchError,
    setIsCreateGroupModalOpen,
    handleLogout,
    handleReconnectIm,
    openSession,
    openDirectSession,
    openConversationDraft,
    openSearchConversationDraft,
    openGroupSession,
    handleSearchUsers,
    handleSearchGroups,
    handleApproveRequest,
    handleRejectRequest,
    loadFriendRequests,
    mobilePlazaContent,
    groupMemberSummary,
    groupsById,
    onViewUserProfile,
    onNavigate,
    selectedSettingsSection,
    onSelectSettingsSection,
  } = props;
  const [settingsQuery, setSettingsQuery] = React.useState('');
  const ownerContact = contactsById[currentUser.id];



  const normalizedSettingsQuery = settingsQuery.trim().toLocaleLowerCase();
  const settingsMenuItems = ([
    {
      section: 'profile',
      label: '个人资料',
      description: '头像、背景与个性签名',
      icon: <IconUser size={18} stroke={2} />,
      color: 'var(--locs-forest)',
    },
    {
      section: 'callHistory',
      label: '通话记录',
      description: '语音、视频与未接通话',
      icon: <IconHistory size={18} stroke={2} />,
      color: 'var(--locs-terracotta)',
    },
    {
      section: 'appearance',
      label: '外观与主题',
      description: '色彩主题、明暗模式与界面风格',
      icon: <IconSun size={18} stroke={2} />,
      color: 'var(--locs-gold)',
    },
    {
      section: 'connection',
      label: '设备与安全',
      description: '查看已授权设备与安全状态',
      icon: <IconShieldLock size={18} stroke={2} />,
      color: 'var(--locs-terracotta)',
    },
    {
      section: 'privacy',
      label: '隐私条款',
      description: '查看隐私条款与服务条款',
      icon: <IconFileDescription size={18} stroke={2} />,
      color: 'var(--locs-brass-green)',
    },
    {
      section: 'about',
      label: '关于 LocsClub',
      description: '客户端与版本信息',
      icon: <IconInfoCircle size={18} stroke={2} />,
      color: 'var(--locs-text-muted)',
    },
  ] satisfies Array<{
    section: SettingsSection;
    label: string;
    description: string;
    icon: React.ReactNode;
    color: string;
  }>).filter((item) => (
    !normalizedSettingsQuery
    || `${item.label} ${item.description}`.toLocaleLowerCase().includes(normalizedSettingsQuery)
  ));

  return (
    <Group
      component="aside"
      data-testid="workspace-list-panel"
      w="100%"
      h="100%"
      wrap="nowrap"
      align="stretch"
      gap={0}
      bg="var(--mantine-color-body)"
    >
      <Box
        flex={1}
        display={{ base: 'flex', sm: 'flex' }}
        style={{ flexDirection: 'column', width: '100%', height: '100%', minWidth: 0, position: 'relative' }}
      >

        <Box
          aria-live="polite"
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            zIndex: 20,
            pointerEvents: imError ? 'auto' : 'none',
          }}
        >
          {imError && (
            <Alert
              icon={<IconBell size={16} />}
              color="terracotta"
              variant="light"
              radius="md"
              withCloseButton
              onClose={handleReconnectIm}
              style={{
                boxShadow: '0 10px 24px rgba(27, 18, 11, 0.12)',
                backdropFilter: 'blur(12px)',
              }}
            >
              {imError}
            </Alert>
          )}
        </Box>

        {activeTab === 'plaza' && mobilePlazaContent && (
          <Box
            hiddenFrom="sm"
            style={{
              flex: 1,
              minHeight: 0,
              paddingTop: 'max(12px, env(safe-area-inset-top, 0px))',
              paddingBottom: 'calc(60px + max(8px, env(safe-area-inset-bottom, 0px)))',
              boxSizing: 'border-box',
            }}
          >
            {mobilePlazaContent}
          </Box>
        )}

        {(activeTab === 'chats' || (activeTab === 'contacts' && contactsSubView === 'list')) && (
          <Box
            px="md"
            py="xs"
            style={{
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--mantine-spacing-xs))',
            }}
          >
            <TextInput
              variant="unstyled"
              placeholder={activeTab === 'chats' ? '搜索会话' : '搜索联系人'}
              value={directoryFilter}
              onChange={(event) => setDirectoryFilter(event.currentTarget.value)}
              styles={{
                input: {
                  border: 'none',
                  backgroundColor: 'var(--control-bg)',
                  boxShadow: 'none',
                  paddingInlineStart: 16,
                  paddingInlineEnd: 48,
                },
              }}
              rightSectionWidth={48}
              rightSection={<IconSearch size={18} stroke={2} color="var(--mantine-color-dimmed)" />}
            />
          </Box>
        )}

        <Box
          data-hidden-mobile={(activeTab === 'plaza' && mobilePlazaContent) ? 'true' : undefined}
          style={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
          }}
        >
          {activeTab === 'chats' && visibleSessions.length === 0 && (
            <Center
              data-testid="chat-list-empty-state"
              style={{
                position: 'absolute',
                inset: 0,
                bottom: 'calc(60px + max(8px, env(safe-area-inset-bottom, 0px)))',
                zIndex: 1,
                pointerEvents: 'none',
                transform: 'translateY(-4vh)',
              }}
            >
              <WorkspaceEmptyState
                icon={<IconMessage size={30} stroke={2} />}
                message="还没有会话"
              />
            </Center>
          )}

          <ScrollArea
            visibleFrom={activeTab === 'plaza' && mobilePlazaContent ? 'sm' : undefined}
            style={{ height: '100%' }}
            offsetScrollbars
            styles={{
              viewport: {
                paddingBottom: 'calc(60px + max(8px, env(safe-area-inset-bottom, 0px)))',
              },
            }}
          >
            {activeTab === 'chats' && (
              <Stack gap="xs" p="xs">
                {visibleSessions.length > 0 && (
                  visibleSessions.map((session) => (
                    <ThreadItem
                      key={session.sessionKey}
                      session={session}
                      avatarUrl={resolveSessionAvatarUrl(session, contactsById, groupsById)}
                      presenceStatus={session.sessionType === 'single'
                        ? presenceByUserId[session.sessionId] ?? 0
                        : undefined}
                      active={activeSessionKey === session.sessionKey}
                      onClick={() => openSession(session.sessionKey)}
                    />
                  ))
                )}
              </Stack>
            )}

            {activeTab === 'settings' && (
              <Stack gap="md" p="md" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--mantine-spacing-md))' }}>
                <Group justify="space-between" px={4}>
                  <Text fw={900} size="xl" ff="var(--mantine-font-family-headings)">设置</Text>
                </Group>

                <TextInput
                  variant="unstyled"
                  value={settingsQuery}
                  onChange={(event) => setSettingsQuery(event.currentTarget.value)}
                  placeholder="搜索设置"
                  aria-label="搜索设置"
                  styles={{
                    input: {
                      border: 'none',
                      backgroundColor: 'var(--control-bg)',
                      boxShadow: 'none',
                      paddingInlineStart: 16,
                      paddingInlineEnd: 48,
                    },
                  }}
                  rightSectionWidth={48}
                  rightSection={<IconSearch size={18} stroke={2} color="var(--mantine-color-dimmed)" />}
                />

                <UnstyledButton
                  onClick={() => onSelectSettingsSection('profile')}
                  aria-label="打开个人资料设置"
                  px="sm"
                  py="md"
                  style={{
                    width: '100%',
                    borderRadius: 'var(--mantine-radius-md)',
                    transition: 'background-color 160ms ease',
                  }}
                  styles={{ root: { '&:hover': { backgroundColor: 'rgba(44, 38, 35, 0.035)' } } }}
                >
                  <Group wrap="nowrap" align="center" gap="md">
                    <UserAvatar
                      id={currentUser.id}
                      name={currentUser.displayName || `用户 #${currentUser.id}`}
                      fallback={initials(currentUser.displayName, String(currentUser.id).slice(-2))}
                      avatarKind="human"
                      avatarUrl={profileAvatarUrl || ownerContact?.avatarUrl}
                      cdnBase={cdnBase}
                      size={48}
                    />
                    <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                      <Text fw={850} size="md" truncate>{currentUser.displayName || '用户'}</Text>
                      <Group gap={6} align="center" mt={2}>
                        <Box
                          w={8}
                          h={8}
                          style={{
                            borderRadius: '50%',
                            backgroundColor: isOnline
                              ? 'var(--mantine-color-green-filled)'
                              : connState === 'connecting'
                                ? 'var(--mantine-color-yellow-filled)'
                                : 'var(--mantine-color-gray-filled)',
                          }}
                        />
                        <Text size="xs" c="dimmed" fw={600}>
                          {isOnline ? '在线' : connState === 'connecting' ? '连接中' : '离线'}
                        </Text>
                      </Group>
                    </Stack>
                  </Group>
                </UnstyledButton>

                <Stack gap={4}>
                  {settingsMenuItems.map((item) => (
                    <SettingsActionItem
                      key={item.section}
                      onClick={() => onSelectSettingsSection(item.section)}
                      label={item.label}
                      description={item.description}
                      icon={item.icon}
                      color={item.color}
                      active={selectedSettingsSection === item.section}
                    />
                  ))}
                  <SettingsActionItem
                    onClick={handleLogout}
                    label="退出登录"
                    description="结束当前账户会话"
                    icon={<IconLogout size={18} stroke={2} />}
                    color="#ef4444"
                    danger
                  />
                </Stack>
                {settingsMenuItems.length === 0 && (
                  <Box py="lg" ta="center">
                    <Text size="sm" c="dimmed">没有匹配的设置项目</Text>
                  </Box>
                )}
              </Stack>
            )}

            {activeTab === 'contacts' && contactsSubView === 'list' && (
              <Box flex={1} style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ScrollArea scrollbarSize={6} flex={1}>
                  <Stack gap="sm" p="xs">
                    <Box px="xs">
                      <Stack gap="xs">
                        <ContactsEntryItem
                          onClick={() => {
                            setContactsSubView('search');
                            setSearchKeyword('');
                            setSearchResults([]);
                            setSearchState('idle');
                            setSearchError(null);
                          }}
                          label="添加联系人"
                          icon={<IconSearch size={18} stroke={2} />}
                        />
                        <ContactsEntryItem
                          onClick={() => { setContactsSubView('requests'); void loadFriendRequests(); }}
                          label="好友申请"
                          badge={pendingRequestCount}
                          icon={<IconUserPlus size={18} stroke={2} />}
                        />
                        <ContactsEntryItem
                          onClick={() => setContactsSubView('groups')}
                          label="我的群组"
                          icon={<IconUsers size={18} stroke={2} />}
                        />
                      </Stack>
                    </Box>
                    <Group px="sm" pt="md">
                      <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={0.6}>联系人</Text>
                    </Group>
                    <Box px="xs">
                      <Stack gap={4}>
                        {visibleContacts.length === 0 ? (
                          <Box py={40} px={20} ta="center">
                            <Center
                              mx="auto"
                              w={54}
                              h={54}
                              c="forestGreen.7"
                              style={{ borderRadius: 18, backgroundColor: 'rgba(26, 77, 58, 0.1)' }}
                            >
                              <IconUsers size={28} stroke={2} />
                            </Center>
                            <Text fw={800} size="md" mt="md">还没有联系人</Text>
                            <Text size="xs" c="dimmed" mt={4}>先添加联系人，再开始聊天</Text>
                          </Box>
                        ) : (
                          visibleContacts.map((contact) => (
                            <ContactItem
                              key={contact.id}
                              id={contact.id}
                              name={contact.name}
                              avatarUrl={contact.avatarUrl}
                              presenceStatus={presenceByUserId[contact.id] ?? 0}
                              onClick={() => openDirectSession(contact.id)}
                              onStartConversation={() => openConversationDraft(contact)}
                              onViewUserProfile={onViewUserProfile}
                            />
                          ))
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </ScrollArea>
              </Box>
            )}

            {activeTab === 'contacts' && contactsSubView === 'groups' && (
              <Box flex={1} style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)', paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--mantine-spacing-sm))' }}>
                  <Box
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px minmax(0, 1fr) 40px',
                      alignItems: 'center',
                    }}
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="返回联系人"
                      title="返回联系人"
                      onClick={() => setContactsSubView('list')}
                    >
                      <IconChevronLeft size={20} stroke={2} />
                    </ActionIcon>
                    <Text size="sm" fw={700} ta="center">我的群组</Text>
                    <ActionIcon
                      variant="subtle"
                      color="terracotta"
                      size="sm"
                      aria-label="新建群组"
                      title="新建群组"
                      onClick={() => setIsCreateGroupModalOpen(true)}
                      style={{ justifySelf: 'end' }}
                    >
                      <IconPlus size={20} stroke={2} />
                    </ActionIcon>
                  </Box>
                </Box>
                <ScrollArea flex={1} p="xs">
                  <Stack gap="sm">
                    <ContactsEntryItem
                      onClick={() => {
                        setContactsSubView('group-search');
                        setGroupSearchKeyword('');
                        setGroupSearchResults([]);
                        setGroupSearchState('idle');
                        setGroupSearchError(null);
                      }}
                      label="搜索群组"
                      icon={<IconSearch size={18} stroke={2} />}
                    />
                    <Stack gap={4}>
                      {visibleGroups.length === 0 ? (
                        <Box py={40} px={20} ta="center">
                          <Center
                            mx="auto"
                            w={54}
                            h={54}
                            c="terracotta.7"
                            style={{ borderRadius: 18, backgroundColor: 'rgba(196, 90, 60, 0.1)' }}
                          >
                            <IconSparkles size={28} stroke={2} />
                          </Center>
                          <Text fw={800} size="md" mt="md">还没有加入群组</Text>
                          <Text size="xs" c="dimmed" mt={4}>加入群组后，会在这里显示</Text>
                        </Box>
                      ) : (
                        visibleGroups.map((group) => (
                          <UnstyledButton
                            key={group.id}
                            px="md"
                            py="sm"
                            onClick={() => openGroupSession(group.id)}
                            style={{ borderRadius: 12 }}
                          >
                            <Group wrap="nowrap" gap="sm">
                              <UserAvatar id={group.id} name={group.name} fallback="GR" avatarKind="group" size={36} />
                              <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                                <Text size="sm" fw={600} truncate>{group.name}</Text>
                                <Text size="xs" c="dimmed" truncate>{groupMemberSummary(group, contactsById, currentUser.id)}</Text>
                              </Stack>
                            </Group>
                          </UnstyledButton>
                        ))
                      )}
                    </Stack>
                  </Stack>
                </ScrollArea>
              </Box>
            )}

            {(contactsSubView === 'search' || contactsSubView === 'group-search') && (
              <Box flex={1} style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)', paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--mantine-spacing-sm))' }}>
                  <Box
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px minmax(0, 1fr) 40px',
                      alignItems: 'center',
                    }}
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={contactsSubView === 'search' ? '返回联系人' : '返回我的群组'}
                      title={contactsSubView === 'search' ? '返回联系人' : '返回我的群组'}
                      onClick={() => setContactsSubView(contactsSubView === 'search' ? 'list' : 'groups')}
                    >
                      <IconChevronLeft size={20} stroke={2} />
                    </ActionIcon>
                    <Text size="sm" fw={700} ta="center">{contactsSubView === 'search' ? '添加联系人' : '搜索群组'}</Text>
                    <Box aria-hidden="true" />
                  </Box>
                </Box>
                <ScrollArea flex={1} p="md">
                  <Stack gap="md">
                    <TextInput
                      variant="unstyled"
                      aria-label={contactsSubView === 'search' ? '搜索联系人' : '搜索群组'}
                      placeholder={contactsSubView === 'search' ? '输入昵称或关键词' : '输入群组名称'}
                      styles={{
                        input: {
                          border: 'none',
                          backgroundColor: 'var(--control-bg)',
                          boxShadow: 'none',
                          paddingInlineStart: 16,
                          paddingInlineEnd: 48,
                        },
                      }}
                      rightSectionWidth={48}
                      rightSectionPointerEvents="all"
                      rightSection={(
                        <ActionIcon
                          variant="transparent"
                          color="terracotta"
                          size={36}
                          aria-label={contactsSubView === 'search' ? '搜索联系人' : '搜索群组'}
                          title={contactsSubView === 'search' ? '搜索联系人' : '搜索群组'}
                          onClick={() => contactsSubView === 'search' ? void handleSearchUsers() : void handleSearchGroups()}
                          disabled={(contactsSubView === 'search' ? !searchKeyword.trim() : !groupSearchKeyword.trim()) || (contactsSubView === 'search' ? searchState : groupSearchState) === 'loading'}
                          style={{ backgroundColor: 'transparent' }}
                        >
                          {(contactsSubView === 'search' ? searchState : groupSearchState) === 'loading'
                            ? <Loader size={16} color="terracotta" />
                            : <IconSearch size={18} stroke={2} />}
                        </ActionIcon>
                      )}
                      value={contactsSubView === 'search' ? searchKeyword : groupSearchKeyword}
                      onChange={(event) => {
                        const val = event.currentTarget.value;
                        if (contactsSubView === 'search') {
                          setSearchKeyword(val);
                          if (!val.trim()) {
                            setSearchResults([]);
                            setSearchState('idle');
                            setSearchError(null);
                          }
                        } else {
                          setGroupSearchKeyword(val);
                          if (!val.trim()) {
                            setGroupSearchResults([]);
                            setGroupSearchState('idle');
                            setGroupSearchError(null);
                          }
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          contactsSubView === 'search' ? void handleSearchUsers() : void handleSearchGroups();
                        }
                      }}
                    />

                    {(contactsSubView === 'search' ? searchError : groupSearchError) && (
                      <Paper bg="red.0" p="sm" radius="md">
                        <Text c="red" size="xs">{contactsSubView === 'search' ? searchError : groupSearchError}</Text>
                      </Paper>
                    )}

                    {(contactsSubView === 'search' ? searchState : groupSearchState) !== 'idle' && (
                      <Stack gap="xs" mt="sm">
                        {contactsSubView === 'search' ? (
                          searchResults.length === 0 ? (
                            <Box style={{ position: 'relative', minHeight: 'calc(100dvh - 220px)' }}>
                              <Center
                                py="xl"
                                style={{ position: 'absolute', top: '38.2%', left: 0, right: 0, transform: 'translateY(-50%)' }}
                              >
                                <WorkspaceEmptyState
                                  icon={<IconUserSearch size={28} stroke={1.7} />}
                                  message="没有找到匹配的成员"
                                />
                              </Center>
                            </Box>
                          ) : (
                            searchResults
                              .filter((entry) => entry.id !== currentUser.id)
                              .map((entry) => (
                                <Paper key={entry.id} withBorder p="sm" radius="md">
                                  <Group justify="space-between" wrap="nowrap">
                                    <Group
                                      gap="sm"
                                      style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                      onClick={() => onViewUserProfile?.(entry.id)}
                                    >
                                      <UserAvatar id={entry.id} name={entry.name} avatarKind="human" size={32} />
                                      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                                        <Text size="sm" fw={600} truncate>{entry.name || `成员 #${entry.id}`}</Text>
                                        <Text size="xs" c="dimmed" truncate>{entry.status || '平台用户'}</Text>
                                      </Stack>
                                    </Group>
                                    {contactsById[entry.id] ? (
                                      <UnstyledButton onClick={() => openSearchConversationDraft(entry)}>
                                        <Text size="xs" c="var(--mantine-color-primary-filled)" fw={600}>发起会话</Text>
                                      </UnstyledButton>
                                    ) : (
                                      <UnstyledButton
                                        onClick={() => {
                                          setFriendRequestTarget(entry);
                                          setFriendRequestNote('');
                                          setIsFriendRequestModalOpen(true);
                                        }}
                                        disabled={sendingRequestByUserId[entry.id]}
                                      >
                                        <Text size="xs" c="var(--mantine-color-primary-filled)" fw={600}>{sendingRequestByUserId[entry.id] ? '发送中…' : '添加'}</Text>
                                      </UnstyledButton>
                                    )}
                                  </Group>
                                </Paper>
                              ))
                          )
                        ) : (
                          groupSearchResults.length === 0 ? (
                            <Box style={{ position: 'relative', minHeight: 'calc(100dvh - 220px)' }}>
                              <Center
                                py="xl"
                                style={{ position: 'absolute', top: '38.2%', left: 0, right: 0, transform: 'translateY(-50%)' }}
                              >
                                <WorkspaceEmptyState
                                  icon={<IconUsers size={28} stroke={1.7} />}
                                  message="没有找到匹配的群组"
                                />
                              </Center>
                            </Box>
                          ) : (
                            groupSearchResults.map((entry) => {
                              const joinedGroup = groupsById[entry.id];
                              return (
                                <Paper key={entry.id} withBorder p="sm" radius="md">
                                  <Group justify="space-between" wrap="nowrap">
                                    <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                      <UserAvatar id={entry.id} name={entry.name} fallback="GR" avatarKind="group" size={32} />
                                      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                                        <Text size="sm" fw={600} truncate>{entry.name || `群组 #${entry.id}`}</Text>
                                        {entry.memberCount > 0 && (
                                          <Text size="xs" c="dimmed" truncate>{entry.memberCount} 人</Text>
                                        )}
                                      </Stack>
                                    </Group>
                                    {joinedGroup ? (
                                      <UnstyledButton onClick={() => openGroupSession(entry.id)}>
                                        <Text size="xs" c="var(--mantine-color-primary-filled)" fw={600}>打开</Text>
                                      </UnstyledButton>
                                    ) : (
                                      <Text size="xs" c="dimmed">暂未加入</Text>
                                    )}
                                  </Group>
                                </Paper>
                              );
                            })
                          )
                        )}
                      </Stack>
                    )}
                  </Stack>
                </ScrollArea>
              </Box>
            )}

            {activeTab === 'contacts' && contactsSubView === 'requests' && (
              <Box flex={1} style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)', paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--mantine-spacing-sm))' }}>
                  <Box
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px minmax(0, 1fr) 40px',
                      alignItems: 'center',
                    }}
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="返回联系人"
                      title="返回联系人"
                      onClick={() => setContactsSubView('list')}
                    >
                      <IconChevronLeft size={20} stroke={2} />
                    </ActionIcon>
                    <Text size="sm" fw={700} ta="center">好友申请</Text>
                    <ActionIcon
                      variant="subtle"
                      color="terracotta"
                      size="sm"
                      aria-label="刷新好友申请"
                      title="刷新好友申请"
                      onClick={() => void loadFriendRequests()}
                      disabled={friendRequestsLoading}
                      style={{ justifySelf: 'end' }}
                    >
                      {friendRequestsLoading ? <Loader size={16} /> : <IconRefresh size={18} stroke={2} />}
                    </ActionIcon>
                  </Box>
                </Box>
                <ScrollArea
                  flex={1}
                  p="xs"
                  styles={{ viewport: { height: '100%' } }}
                >
                  <Stack
                    gap="xs"
                    style={{ position: 'relative', minHeight: 'calc(100dvh - 150px)' }}
                  >
                    {friendRequestsLoading ? (
                      <Center style={{ position: 'absolute', top: '38.2%', left: 0, right: 0, transform: 'translateY(-50%)' }}>
                        <Loader size="sm" color="terracotta" />
                      </Center>
                    ) : friendRequests.length === 0 ? (
                      <Center px={20} style={{ position: 'absolute', top: '38.2%', left: 0, right: 0, transform: 'translateY(-50%)' }}>
                        <WorkspaceEmptyState
                          icon={<IconUsers size={28} stroke={1.7} />}
                          message="暂时没有新的好友申请"
                        />
                      </Center>
                    ) : (
                      friendRequests.map((request) => {
                        const action = friendRequestActionById[request.requestId];
                        return (
                          <Paper key={request.requestId} withBorder p="sm" radius="md">
                            <Group justify="space-between" wrap="nowrap">
                              <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                <UserAvatar id={request.fromUserId} name={request.fromUserName} fallback="FR" avatarKind="human" size={32} />
                                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                                  <Text size="sm" fw={600} truncate>{request.fromUserName || `成员 #${request.fromUserId}`}</Text>
                                  <Text size="xs" c="dimmed" truncate>{request.verifyMessage || '请求建立关系'}</Text>
                                </Stack>
                              </Group>
                              <Group gap={6}>
                                <UnstyledButton
                                  onClick={() => void handleRejectRequest(request)}
                                  disabled={Boolean(action)}
                                  p={6}
                                  style={{ display: 'flex', borderRadius: 'var(--mantine-radius-md)', color: 'var(--mantine-color-red-7)', backgroundColor: 'rgba(250, 82, 82, 0.08)' }}
                                >
                                  <IconX size={14} stroke={2.4} />
                                </UnstyledButton>
                                <UnstyledButton
                                  onClick={() => void handleApproveRequest(request)}
                                  disabled={Boolean(action)}
                                  p={6}
                                  style={{ display: 'flex', borderRadius: 'var(--mantine-radius-md)', color: 'var(--mantine-color-forestGreen-7)', backgroundColor: 'rgba(26, 77, 58, 0.1)' }}
                                >
                                  <IconCheck size={14} stroke={2.4} />
                                </UnstyledButton>
                              </Group>
                            </Group>
                          </Paper>
                        );
                      })
                    )}
                  </Stack>
                </ScrollArea>
              </Box>
            )}
          </ScrollArea>
        </Box>

        <WorkspaceListBottomNav
          activeTab={activeTab}
          totalUnreadCount={totalUnreadCount}
          pendingRequestCount={pendingRequestCount}
          onNavigate={onNavigate}
        />
      </Box>
    </Group>
  );
}
